import { useState, useEffect, useRef, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import { motion } from 'framer-motion';
import { 
  Plus, Edit2, Trash2, Loader2, MoreVertical, 
  FolderTree, Image as ImageIcon, ToggleLeft, ToggleRight,
  ChevronDown, X, Check, Grid3X3, Languages
} from 'lucide-react';
import { Button } from '../components/ui/button';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter, DialogDescription } from '../components/ui/dialog';
import { Input } from '../components/ui/input';
import { Switch } from '../components/ui/switch';
import { useLanguage } from '../context/LanguageContext';
import { useAuth } from '../context/AuthContext';
import DashboardLayout from '../components/DashboardLayout';
import { TranslateButton } from '../components/TranslateButton';
import { toast } from 'sonner';
import axios from 'axios';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;

// Validation: ensure no script crossover (Arabic letters in EN field or vice versa).
// All punctuation, digits, spaces and special chars are allowed — translations
// often legitimately contain parentheses, slashes, ampersands, etc.
const ARABIC_LETTERS_RE = /[\u0600-\u06FF\u0750-\u077F\u08A0-\u08FF]/;
const LATIN_LETTERS_RE = /[A-Za-z]/;

const isArabicOnly = (text) => {
  if (!text) return true;
  return !LATIN_LETTERS_RE.test(text);   // no English letters
};

const isEnglishOnly = (text) => {
  if (!text) return true;
  return !ARABIC_LETTERS_RE.test(text);   // no Arabic letters
};

export default function CategoriesManagement() {
  const navigate = useNavigate();
  const { language } = useLanguage();
  const { token } = useAuth();
  const fileInputRef = useRef(null);
  
  // Scroll to top
  useEffect(() => {
    window.scrollTo(0, 0);
  }, []);

  // State
  const [categories, setCategories] = useState([]);
  const [loading, setLoading] = useState(true);
  const [activeView, setActiveView] = useState('categories'); // categories, subcategories, add-subcategory
  const [selectedCategory, setSelectedCategory] = useState(null);
  const [subcategories, setSubcategories] = useState([]);
  const [loadingSubcategories, setLoadingSubcategories] = useState(false);
  
  // Modal states
  const [showAddModal, setShowAddModal] = useState(false);
  const [showEditModal, setShowEditModal] = useState(false);
  const [showDeleteModal, setShowDeleteModal] = useState(false);
  const [showSubcategoryModal, setShowSubcategoryModal] = useState(false);
  const [editingItem, setEditingItem] = useState(null);
  const [deletingItem, setDeletingItem] = useState(null);
  const [saving, setSaving] = useState(false);
  
  // Form state
  const [formData, setFormData] = useState({
    name_ar: '',
    name_en: '',
    description_ar: '',
    description_en: '',
    image: '',
    is_active: true
  });

  // التصنيفات الفرعية عند إضافة تصنيف جديد
  const [newSubcategories, setNewSubcategories] = useState([{ _uid: 'sub-0', name_ar: '', name_en: '', description_ar: '', description_en: '', image: '' }]);
  
  // Dropdown menu state
  const [openMenuId, setOpenMenuId] = useState(null);

  // Validation errors
  const [errors, setErrors] = useState({});

  // Translation states
  const [translatingAr, setTranslatingAr] = useState(false);
  const [translatingEn, setTranslatingEn] = useState(false);
  const [translatingSubAr, setTranslatingSubAr] = useState({});
  const [translatingSubEn, setTranslatingSubEn] = useState({});

  const getHeaders = () => ({
    'Authorization': `Bearer ${token}`,
    'Content-Type': 'application/json'
  });

  // دالة الترجمة
  const translateText = useCallback(async (text, direction) => {
    try {
      const res = await axios.post(
        `${BACKEND_URL}/api/translate`,
        { text, direction },
        { headers: getHeaders() }
      );
      return res.data.translated;
    } catch (err) {
      console.error('Translation error:', err);
      return null;
    }
  }, [token]);

  // ترجمة الاسم العربي إلى الإنجليزي
  const translateArToEn = async () => {
    if (!formData.name_ar.trim() || translatingAr) return;
    setTranslatingAr(true);
    const translated = await translateText(formData.name_ar, 'ar_to_en');
    if (translated && isEnglishOnly(translated)) {
      setFormData(prev => ({ ...prev, name_en: translated }));
    }
    setTranslatingAr(false);
  };

  // ترجمة الاسم الإنجليزي إلى العربي
  const translateEnToAr = async () => {
    if (!formData.name_en.trim() || translatingEn) return;
    setTranslatingEn(true);
    const translated = await translateText(formData.name_en, 'en_to_ar');
    if (translated && isArabicOnly(translated)) {
      setFormData(prev => ({ ...prev, name_ar: translated }));
    }
    setTranslatingEn(false);
  };

  // ترجمة التصنيف الفرعي من العربي للإنجليزي
  const translateSubArToEn = async (index) => {
    const sub = newSubcategories[index];
    if (!sub.name_ar.trim() || translatingSubAr[index]) return;
    setTranslatingSubAr(prev => ({ ...prev, [index]: true }));
    const translated = await translateText(sub.name_ar, 'ar_to_en');
    if (translated && isEnglishOnly(translated)) {
      updateSubcategoryField(index, 'name_en', translated);
    }
    setTranslatingSubAr(prev => ({ ...prev, [index]: false }));
  };

  // ترجمة التصنيف الفرعي من الإنجليزي للعربي
  const translateSubEnToAr = async (index) => {
    const sub = newSubcategories[index];
    if (!sub.name_en.trim() || translatingSubEn[index]) return;
    setTranslatingSubEn(prev => ({ ...prev, [index]: true }));
    const translated = await translateText(sub.name_en, 'en_to_ar');
    if (translated && isArabicOnly(translated)) {
      updateSubcategoryField(index, 'name_ar', translated);
    }
    setTranslatingSubEn(prev => ({ ...prev, [index]: false }));
  };

  // Fetch categories
  const fetchCategories = async () => {
    setLoading(true);
    try {
      const res = await axios.get(`${BACKEND_URL}/api/admin/categories`, { headers: getHeaders() });
      setCategories(res.data.data || []);
    } catch (err) {
      console.error('Error fetching categories:', err);
      toast.error(language === 'ar' ? 'فشل في جلب التصنيفات' : 'Failed to fetch categories');
    } finally {
      setLoading(false);
    }
  };

  // Fetch subcategories
  const fetchSubcategories = async (categoryId) => {
    setLoadingSubcategories(true);
    try {
      const res = await axios.get(
        `${BACKEND_URL}/api/admin/categories/${categoryId}/subcategories`,
        { headers: getHeaders() }
      );
      setSelectedCategory(res.data.category);
      setSubcategories(res.data.data || []);
    } catch (err) {
      console.error('Error fetching subcategories:', err);
      toast.error(language === 'ar' ? 'فشل في جلب التصنيفات الفرعية' : 'Failed to fetch subcategories');
    } finally {
      setLoadingSubcategories(false);
    }
  };

  // Add category with subcategories
  const addCategory = async () => {
    // التحقق من الاسم العربي
    if (!formData.name_ar.trim()) {
      toast.error(language === 'ar' ? 'يرجى إدخال الاسم بالعربي' : 'Please enter Arabic name');
      return;
    }
    if (!isArabicOnly(formData.name_ar.trim())) {
      toast.error(language === 'ar' ? 'الاسم بالعربي يجب أن يحتوي على حروف عربية فقط' : 'Arabic name must contain Arabic characters only');
      return;
    }
    
    // التحقق من الاسم الإنجليزي
    if (!formData.name_en.trim()) {
      toast.error(language === 'ar' ? 'يرجى إدخال الاسم بالإنجليزي' : 'Please enter English name');
      return;
    }
    if (!isEnglishOnly(formData.name_en.trim())) {
      toast.error(language === 'ar' ? 'الاسم بالإنجليزي يجب أن يحتوي على حروف إنجليزية فقط' : 'English name must contain English characters only');
      return;
    }
    
    // التحقق من الصورة
    if (!formData.image) {
      toast.error(language === 'ar' ? 'يرجى رفع صورة التصنيف' : 'Please upload category image');
      return;
    }
    
    // التحقق من التصنيفات الفرعية
    const nonEmptySubs = newSubcategories.filter(sub => sub.name_ar.trim() || sub.name_en.trim());
    // أي صف فيه لغة واحدة فقط = خطأ (يجب الاثنتين)
    const partial = nonEmptySubs.find(sub => !sub.name_ar.trim() || !sub.name_en.trim());
    if (partial) {
      toast.error(language === 'ar'
        ? 'كل تصنيف فرعي يجب أن يحتوي على الاسم العربي والإنجليزي معاً'
        : 'Every subcategory must have BOTH Arabic and English names');
      return;
    }
    const validSubcategories = nonEmptySubs;
    if (validSubcategories.length === 0) {
      toast.error(language === 'ar' ? 'يرجى إضافة تصنيف فرعي واحد على الأقل' : 'Please add at least one subcategory');
      return;
    }
    
    // التحقق من لغة التصنيفات الفرعية
    for (const sub of validSubcategories) {
      if (!isArabicOnly(sub.name_ar.trim())) {
        toast.error(language === 'ar' ? 'التصنيف الفرعي بالعربي يجب أن يحتوي على حروف عربية فقط' : 'Subcategory Arabic name must contain Arabic characters only');
        return;
      }
      if (!isEnglishOnly(sub.name_en.trim())) {
        toast.error(language === 'ar' ? 'التصنيف الفرعي بالإنجليزي يجب أن يحتوي على حروف إنجليزية فقط' : 'Subcategory English name must contain English characters only');
        return;
      }
    }
    
    setSaving(true);
    try {
      // إنشاء التصنيف الرئيسي
      const categoryRes = await axios.post(`${BACKEND_URL}/api/admin/categories`, formData, { headers: getHeaders() });
      const newCategoryId = categoryRes.data.data.id;
      
      // إضافة التصنيفات الفرعية
      for (const sub of validSubcategories) {
        await axios.post(
          `${BACKEND_URL}/api/admin/categories/${newCategoryId}/subcategories`,
          { name_ar: sub.name_ar.trim(), name_en: sub.name_en.trim(), is_active: true },
          { headers: getHeaders() }
        );
      }
      
      toast.success(language === 'ar' ? 'تم إضافة التصنيف بنجاح' : 'Category added successfully');
      setShowAddModal(false);
      resetForm();
      fetchCategories();
    } catch (err) {
      toast.error(err.response?.data?.detail || (language === 'ar' ? 'فشل في الإضافة' : 'Failed to add'));
    } finally {
      setSaving(false);
    }
  };

  // Update category
  const updateCategory = async () => {
    // التحقق من الاسم العربي
    if (!formData.name_ar.trim()) {
      toast.error(language === 'ar' ? 'يرجى إدخال الاسم بالعربي' : 'Please enter Arabic name');
      return;
    }
    if (!isArabicOnly(formData.name_ar.trim())) {
      toast.error(language === 'ar' ? 'الاسم بالعربي يجب أن يحتوي على حروف عربية فقط' : 'Arabic name must contain Arabic characters only');
      return;
    }
    
    // التحقق من الاسم الإنجليزي
    if (!formData.name_en.trim()) {
      toast.error(language === 'ar' ? 'يرجى إدخال الاسم بالإنجليزي' : 'Please enter English name');
      return;
    }
    if (!isEnglishOnly(formData.name_en.trim())) {
      toast.error(language === 'ar' ? 'الاسم بالإنجليزي يجب أن يحتوي على حروف إنجليزية فقط' : 'English name must contain English characters only');
      return;
    }
    
    // التحقق من الصورة
    if (!formData.image) {
      toast.error(language === 'ar' ? 'يرجى رفع صورة التصنيف' : 'Please upload category image');
      return;
    }
    
    setSaving(true);
    try {
      await axios.put(
        `${BACKEND_URL}/api/admin/categories/${editingItem.id}`,
        formData,
        { headers: getHeaders() }
      );
      toast.success(language === 'ar' ? 'تم تحديث التصنيف بنجاح' : 'Category updated successfully');
      setShowEditModal(false);
      setEditingItem(null);
      resetForm();
      fetchCategories();
    } catch (err) {
      toast.error(err.response?.data?.detail || (language === 'ar' ? 'فشل في التحديث' : 'Failed to update'));
    } finally {
      setSaving(false);
    }
  };

  // Toggle category
  const toggleCategory = async (categoryId) => {
    try {
      const res = await axios.put(
        `${BACKEND_URL}/api/admin/categories/${categoryId}/toggle`,
        {},
        { headers: getHeaders() }
      );
      toast.success(res.data.message);
      fetchCategories();
    } catch (err) {
      toast.error(err.response?.data?.detail || (language === 'ar' ? 'فشل في تغيير الحالة' : 'Failed to toggle'));
    }
  };

  // Delete category
  const deleteCategory = async () => {
    setSaving(true);
    try {
      await axios.delete(
        `${BACKEND_URL}/api/admin/categories/${deletingItem.id}`,
        { headers: getHeaders() }
      );
      toast.success(language === 'ar' ? 'تم حذف التصنيف بنجاح' : 'Category deleted successfully');
      setShowDeleteModal(false);
      setDeletingItem(null);
      fetchCategories();
    } catch (err) {
      toast.error(err.response?.data?.detail || (language === 'ar' ? 'فشل في الحذف' : 'Failed to delete'));
    } finally {
      setSaving(false);
    }
  };

  // Subcategory functions
  const addSubcategory = async () => {
    // التحقق من الاسم العربي
    if (!formData.name_ar.trim()) {
      toast.error(language === 'ar' ? 'يرجى إدخال الاسم بالعربي' : 'Please enter Arabic name');
      return;
    }
    if (!isArabicOnly(formData.name_ar.trim())) {
      toast.error(language === 'ar' ? 'الاسم بالعربي يجب أن يحتوي على حروف عربية فقط' : 'Arabic name must contain Arabic characters only');
      return;
    }
    
    // التحقق من الاسم الإنجليزي
    if (!formData.name_en.trim()) {
      toast.error(language === 'ar' ? 'يرجى إدخال الاسم بالإنجليزي' : 'Please enter English name');
      return;
    }
    if (!isEnglishOnly(formData.name_en.trim())) {
      toast.error(language === 'ar' ? 'الاسم بالإنجليزي يجب أن يحتوي على حروف إنجليزية فقط' : 'English name must contain English characters only');
      return;
    }
    
    setSaving(true);
    try {
      await axios.post(
        `${BACKEND_URL}/api/admin/categories/${selectedCategory.id}/subcategories`,
        { name_ar: formData.name_ar.trim(), name_en: formData.name_en.trim(), is_active: formData.is_active },
        { headers: getHeaders() }
      );
      toast.success(language === 'ar' ? 'تم إضافة التصنيف الفرعي بنجاح' : 'Subcategory added successfully');
      setShowSubcategoryModal(false);
      resetForm();
      fetchSubcategories(selectedCategory.id);
    } catch (err) {
      toast.error(err.response?.data?.detail || (language === 'ar' ? 'فشل في الإضافة' : 'Failed to add'));
    } finally {
      setSaving(false);
    }
  };

  const updateSubcategory = async () => {
    // التحقق من الاسم العربي
    if (!formData.name_ar.trim()) {
      toast.error(language === 'ar' ? 'يرجى إدخال الاسم بالعربي' : 'Please enter Arabic name');
      return;
    }
    if (!isArabicOnly(formData.name_ar.trim())) {
      toast.error(language === 'ar' ? 'الاسم بالعربي يجب أن يحتوي على حروف عربية فقط' : 'Arabic name must contain Arabic characters only');
      return;
    }
    
    // التحقق من الاسم الإنجليزي
    if (!formData.name_en.trim()) {
      toast.error(language === 'ar' ? 'يرجى إدخال الاسم بالإنجليزي' : 'Please enter English name');
      return;
    }
    if (!isEnglishOnly(formData.name_en.trim())) {
      toast.error(language === 'ar' ? 'الاسم بالإنجليزي يجب أن يحتوي على حروف إنجليزية فقط' : 'English name must contain English characters only');
      return;
    }
    
    setSaving(true);
    try {
      await axios.put(
        `${BACKEND_URL}/api/admin/subcategories/${editingItem.id}`,
        { name_ar: formData.name_ar.trim(), name_en: formData.name_en.trim(), is_active: formData.is_active },
        { headers: getHeaders() }
      );
      toast.success(language === 'ar' ? 'تم تحديث التصنيف الفرعي بنجاح' : 'Subcategory updated successfully');
      setShowEditModal(false);
      setEditingItem(null);
      resetForm();
      fetchSubcategories(selectedCategory.id);
    } catch (err) {
      toast.error(err.response?.data?.detail || (language === 'ar' ? 'فشل في التحديث' : 'Failed to update'));
    } finally {
      setSaving(false);
    }
  };

  const toggleSubcategory = async (subcategoryId) => {
    try {
      const res = await axios.put(
        `${BACKEND_URL}/api/admin/subcategories/${subcategoryId}/toggle`,
        {},
        { headers: getHeaders() }
      );
      toast.success(res.data.message);
      fetchSubcategories(selectedCategory.id);
    } catch (err) {
      toast.error(err.response?.data?.detail || (language === 'ar' ? 'فشل في تغيير الحالة' : 'Failed to toggle'));
    }
  };

  const deleteSubcategory = async () => {
    setSaving(true);
    try {
      await axios.delete(
        `${BACKEND_URL}/api/admin/subcategories/${deletingItem.id}`,
        { headers: getHeaders() }
      );
      toast.success(language === 'ar' ? 'تم حذف التصنيف الفرعي بنجاح' : 'Subcategory deleted successfully');
      setShowDeleteModal(false);
      setDeletingItem(null);
      fetchSubcategories(selectedCategory.id);
    } catch (err) {
      toast.error(err.response?.data?.detail || (language === 'ar' ? 'فشل في الحذف' : 'Failed to delete'));
    } finally {
      setSaving(false);
    }
  };

  const resetForm = () => {
    setFormData({ name_ar: '', name_en: '', description_ar: '', description_en: '', image: '', is_active: true });
    setNewSubcategories([{ _uid: `sub-${Date.now()}`, name_ar: '', name_en: '' }]);
    setErrors({});
  };

  // إضافة تصنيف فرعي جديد في النموذج
  const addNewSubcategoryField = () => {
    setNewSubcategories([...newSubcategories, { _uid: `sub-${Date.now()}-${Math.random().toString(36).slice(2, 8)}`, name_ar: '', name_en: '' }]);
  };

  // حذف تصنيف فرعي من النموذج
  const removeSubcategoryField = (index) => {
    if (newSubcategories.length > 1) {
      setNewSubcategories(newSubcategories.filter((_, i) => i !== index));
    }
  };

  // تحديث تصنيف فرعي في النموذج
  const updateSubcategoryField = (index, field, value) => {
    const updated = [...newSubcategories];
    updated[index][field] = value;
    setNewSubcategories(updated);
  };

  // Image upload handler
  const handleImageUpload = (e) => {
    const file = e.target.files[0];
    if (file) {
      if (file.size > 2 * 1024 * 1024) {
        toast.error(language === 'ar' ? 'حجم الصورة يجب أن يكون أقل من 2 ميجا' : 'Image size must be less than 2MB');
        return;
      }
      const reader = new FileReader();
      reader.onloadend = () => {
        setFormData(prev => ({ ...prev, image: reader.result }));
      };
      reader.readAsDataURL(file);
    }
  };

  useEffect(() => {
    fetchCategories();
  }, []);

  // Close dropdown when clicking outside
  useEffect(() => {
    const handleClickOutside = () => setOpenMenuId(null);
    document.addEventListener('click', handleClickOutside);
    return () => document.removeEventListener('click', handleClickOutside);
  }, []);

  return (
    <DashboardLayout
      title={activeView === 'categories' ? 'إدارة تصنيفات المتاجر' : `التصنيفات الفرعية - ${selectedCategory?.name_ar || ''}`}
      titleEn={activeView === 'categories' ? 'Store Categories Management' : `Subcategories - ${selectedCategory?.name_en || ''}`}
      subtitle={activeView === 'categories'
        ? `${categories.length} تصنيف`
        : `${subcategories.length} تصنيف فرعي`}
      subtitleEn={activeView === 'categories'
        ? `${categories.length} categories`
        : `${subcategories.length} subcategories`}
      screenCode="BIZ-C01"
      backPath="/control/cloudgm-business"
      onBack={() => (activeView === 'categories' ? navigate('/control/cloudgm-business') : setActiveView('categories'))}
      breadcrumb={[
        { labelAr: 'التحكم والمعلومات للمنصات', labelEn: 'Platform Control & Info', path: '/dashboard?view=platform-control' },
        { labelAr: 'CLOUDGM FZ LLC (للتحكم)', labelEn: 'CLOUDGM FZ LLC (Control)', path: '/dashboard?view=platform-control&sub=fzlc' },
        { labelAr: 'التحكم والمعلومات (منصة التجار)', labelEn: 'Control & Info (Merchants)', path: '/control/cloudgm-business' },
        { labelAr: 'إدارة تصنيفات المتاجر', labelEn: 'Store Categories Management' },
      ]}
    >
      {/* Main Content */}
      <main className="relative z-10 py-8">
        <div className="max-w-6xl mx-auto px-4 sm:px-6 lg:px-8">
          {/* Action Bar */}
          <motion.div 
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            className="flex items-center justify-between mb-8"
          >
            <div className="flex items-center gap-4">
              <div className="w-14 h-14 rounded-2xl bg-emerald-500/10 flex items-center justify-center border border-emerald-500/20">
                <Grid3X3 className="h-7 w-7 text-emerald-400" />
              </div>
              <div>
                <h2 className="text-xl font-bold text-white">
                  {activeView === 'categories' 
                    ? (language === 'ar' ? 'التصنيفات الرئيسية' : 'Main Categories')
                    : (language === 'ar' ? 'التصنيفات الفرعية' : 'Subcategories')
                  }
                </h2>
                <p className="text-sm text-[#71717A]">
                  {language === 'ar' ? 'إدارة وتنظيم التصنيفات' : 'Manage and organize categories'}
                </p>
              </div>
            </div>

            <div className="flex items-center gap-3">
              {activeView === 'categories' && (
                <motion.button
                  whileHover={{ scale: 1.02 }}
                  whileTap={{ scale: 0.98 }}
                  onClick={() => { resetForm(); setShowAddModal(true); }}
                  className="flex items-center gap-2 px-5 py-2.5 bg-emerald-500 text-white font-bold rounded-xl hover:bg-emerald-600 transition-all"
                  data-testid="add-category-btn"
                >
                  <Plus className="h-5 w-5" />
                  {language === 'ar' ? 'تصنيف جديد' : 'New Category'}
                </motion.button>
              )}
              {activeView === 'subcategories' && (
                <motion.button
                  whileHover={{ scale: 1.02 }}
                  whileTap={{ scale: 0.98 }}
                  onClick={() => { resetForm(); setShowSubcategoryModal(true); }}
                  className="flex items-center gap-2 px-5 py-2.5 bg-emerald-500 text-white font-bold rounded-xl hover:bg-emerald-600 transition-all"
                  data-testid="add-subcategory-btn"
                >
                  <Plus className="h-5 w-5" />
                  {language === 'ar' ? 'تصنيف فرعي جديد' : 'New Subcategory'}
                </motion.button>
              )}
            </div>
          </motion.div>

        {/* Categories Grid */}
        {activeView === 'categories' && (
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ delay: 0.1 }}
          >
            {loading ? (
              <div className="flex items-center justify-center py-20">
                <Loader2 className="h-10 w-10 text-emerald-400 animate-spin" />
              </div>
            ) : categories.length === 0 ? (
              <div className="text-center py-20 bg-white/[0.02] backdrop-blur-xl rounded-2xl border border-white/10">
                <Grid3X3 className="h-16 w-16 text-[#71717A] mx-auto mb-4" />
                <p className="text-[#A1A1AA] text-lg">
                  {language === 'ar' ? 'لا توجد تصنيفات بعد' : 'No categories yet'}
                </p>
                <motion.button
                  whileHover={{ scale: 1.02 }}
                  whileTap={{ scale: 0.98 }}
                  onClick={() => { resetForm(); setShowAddModal(true); }}
                  className="mt-4 flex items-center gap-2 px-5 py-2.5 bg-emerald-500 text-white font-bold rounded-xl hover:bg-emerald-600 mx-auto transition-all"
                >
                  <Plus className="h-5 w-5" />
                  {language === 'ar' ? 'أضف أول تصنيف' : 'Add first category'}
                </motion.button>
              </div>
            ) : (
              <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4 gap-6">
                {categories.map((category, index) => (
                  <motion.div
                    key={category.id}
                    initial={{ opacity: 0, y: 20 }}
                    animate={{ opacity: 1, y: 0 }}
                    transition={{ delay: index * 0.05 }}
                    className="group bg-white/[0.02] backdrop-blur-xl rounded-2xl border border-white/10 overflow-hidden hover:border-emerald-500/40 transition-all"
                    data-testid={`category-card-${category.id}`}
                  >
                    {/* Category Image */}
                    <div className="h-40 bg-gradient-to-br from-white/5 to-transparent flex items-center justify-center relative">
                      {category.image ? (
                        <img
                          src={category.image}
                          alt={category.name_ar}
                          className="w-full h-full object-cover"
                        />
                      ) : (
                        <ImageIcon className="h-16 w-16 text-white/10" />
                      )}
                      {/* Status Badge */}
                      <div className={`absolute top-3 left-3 px-3 py-1 rounded-full text-xs font-medium ${
                        category.is_active 
                          ? 'bg-emerald-500/20 text-emerald-400 border border-emerald-500/30' 
                          : 'bg-white/10 text-[#71717A] border border-white/10'
                      }`}>
                        {category.is_active 
                          ? (language === 'ar' ? 'نشط' : 'Active')
                          : (language === 'ar' ? 'متوقف' : 'Inactive')
                        }
                      </div>
                      {/* Menu Button */}
                      <div className="absolute top-3 right-3">
                        <button
                          onClick={(e) => {
                            e.stopPropagation();
                            setOpenMenuId(openMenuId === category.id ? null : category.id);
                          }}
                          className="w-8 h-8 bg-black/50 backdrop-blur-sm rounded-lg flex items-center justify-center hover:bg-black/70 transition-colors"
                          data-testid={`category-menu-${category.id}`}
                        >
                          <MoreVertical className="h-4 w-4 text-white" />
                        </button>
                        {/* Dropdown Menu */}
                        {openMenuId === category.id && (
                          <div 
                            className="absolute right-0 mt-2 w-48 bg-[#1a1a1a] backdrop-blur-xl rounded-xl shadow-lg border border-white/10 py-2 z-50"
                            onClick={(e) => e.stopPropagation()}
                          >
                            <button
                              onClick={() => {
                                setEditingItem(category);
                                setFormData({
                                  name_ar: category.name_ar,
                                  name_en: category.name_en,
                                  image: category.image || '',
                                  is_active: category.is_active
                                });
                                setShowEditModal(true);
                                setOpenMenuId(null);
                              }}
                              className="w-full px-4 py-2 text-right text-white hover:bg-white/5 flex items-center gap-2"
                            >
                              <Edit2 className="h-4 w-4" />
                              {language === 'ar' ? 'تعديل' : 'Edit'}
                            </button>
                            <button
                              onClick={() => {
                                setActiveView('subcategories');
                                fetchSubcategories(category.id);
                                setOpenMenuId(null);
                              }}
                              className="w-full px-4 py-2 text-right text-white hover:bg-[#3a3a5e] flex items-center gap-2"
                            >
                              <FolderTree className="h-4 w-4" />
                              {language === 'ar' ? 'التصنيفات الفرعية' : 'Subcategories'}
                            </button>
                            <button
                              onClick={() => {
                                toggleCategory(category.id);
                                setOpenMenuId(null);
                              }}
                              className="w-full px-4 py-2 text-right text-amber-400 hover:bg-amber-500/10 flex items-center gap-2"
                            >
                              {category.is_active ? (
                                <>
                                  <ToggleLeft className="h-4 w-4" />
                                  {language === 'ar' ? 'إيقاف' : 'Deactivate'}
                                </>
                              ) : (
                                <>
                                  <ToggleRight className="h-4 w-4" />
                                  {language === 'ar' ? 'تفعيل' : 'Activate'}
                                </>
                              )}
                            </button>
                            <hr className="my-2 border-[#3a3a5e]" />
                            <button
                              onClick={() => {
                                setDeletingItem(category);
                                setShowDeleteModal(true);
                                setOpenMenuId(null);
                              }}
                              className="w-full px-4 py-2 text-right text-red-400 hover:bg-red-500/10 flex items-center gap-2"
                            >
                              <Trash2 className="h-4 w-4" />
                              {language === 'ar' ? 'حذف' : 'Delete'}
                            </button>
                          </div>
                        )}
                      </div>
                    </div>
                    {/* Category Info */}
                    <div className="p-4">
                      <h3 className="text-lg font-bold text-white mb-1">{category.name_ar}</h3>
                      <p className="text-sm text-[#A1A1AA] mb-3">{category.name_en}</p>
                      <div className="flex items-center justify-between">
                        <span className="text-xs text-[#71717A]">
                          {language === 'ar' 
                            ? `${category.subcategories_count || 0} تصنيف فرعي`
                            : `${category.subcategories_count || 0} subcategories`
                          }
                        </span>
                        <Button
                          onClick={() => {
                            setActiveView('subcategories');
                            fetchSubcategories(category.id);
                          }}
                          variant="ghost"
                          size="sm"
                          className="text-emerald-400 hover:text-emerald-300 hover:bg-emerald-500/10"
                        >
                          <FolderTree className="h-4 w-4 me-1" />
                          {language === 'ar' ? 'عرض' : 'View'}
                        </Button>
                      </div>
                    </div>
                  </motion.div>
                ))}
              </div>
            )}
          </motion.div>
        )}

        {/* Subcategories View */}
        {activeView === 'subcategories' && (
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ delay: 0.1 }}
          >
            {loadingSubcategories ? (
              <div className="flex items-center justify-center py-20">
                <Loader2 className="h-10 w-10 text-emerald-400 animate-spin" />
              </div>
            ) : (
              <div className="bg-white/[0.02] backdrop-blur-xl rounded-2xl border border-white/10 overflow-hidden">
                <table className="w-full">
                  <thead>
                    <tr className="border-b border-white/10">
                      <th className="text-right text-[#71717A] text-sm font-medium py-4 px-6">
                        {language === 'ar' ? 'الاسم بالعربي' : 'Arabic Name'}
                      </th>
                      <th className="text-right text-[#71717A] text-sm font-medium py-4 px-6">
                        {language === 'ar' ? 'الاسم بالإنجليزي' : 'English Name'}
                      </th>
                      <th className="text-center text-[#71717A] text-sm font-medium py-4 px-6">
                        {language === 'ar' ? 'الحالة' : 'Status'}
                      </th>
                      <th className="text-center text-[#71717A] text-sm font-medium py-4 px-6">
                        {language === 'ar' ? 'الإجراءات' : 'Actions'}
                      </th>
                    </tr>
                  </thead>
                  <tbody>
                    {subcategories.map((subcat) => (
                      <tr 
                        key={subcat.id} 
                        className="border-b border-white/5 hover:bg-white/[0.02]"
                        data-testid={`subcategory-row-${subcat.id}`}
                      >
                        <td className="py-4 px-6">
                          <span className="text-white font-medium">{subcat.name_ar}</span>
                        </td>
                        <td className="py-4 px-6 text-[#A1A1AA]">{subcat.name_en}</td>
                        <td className="py-4 px-6 text-center">
                          <span className={`px-3 py-1 rounded-full text-xs font-medium ${
                            subcat.is_active 
                              ? 'bg-green-500/20 text-green-400' 
                              : 'bg-gray-500/20 text-gray-400'
                          }`}>
                            {subcat.is_active 
                              ? (language === 'ar' ? 'نشط' : 'Active')
                              : (language === 'ar' ? 'متوقف' : 'Inactive')
                            }
                          </span>
                        </td>
                        <td className="py-4 px-6">
                          <div className="flex items-center justify-center gap-2">
                            <Button
                              onClick={() => {
                                setEditingItem(subcat);
                                setFormData({
                                  name_ar: subcat.name_ar,
                                  name_en: subcat.name_en,
                                  is_active: subcat.is_active,
                                  image: ''
                                });
                                setShowEditModal(true);
                              }}
                              variant="ghost"
                              size="sm"
                              className="text-blue-400 hover:text-blue-300 hover:bg-blue-500/10"
                            >
                              <Edit2 className="h-4 w-4" />
                            </Button>
                            <Button
                              onClick={() => toggleSubcategory(subcat.id)}
                              variant="ghost"
                              size="sm"
                              className="text-amber-400 hover:text-amber-300 hover:bg-amber-500/10"
                            >
                              {subcat.is_active ? (
                                <ToggleLeft className="h-4 w-4" />
                              ) : (
                                <ToggleRight className="h-4 w-4" />
                              )}
                            </Button>
                            <Button
                              onClick={() => {
                                setDeletingItem(subcat);
                                setShowDeleteModal(true);
                              }}
                              variant="ghost"
                              size="sm"
                              className="text-red-400 hover:text-red-300 hover:bg-red-500/10"
                            >
                              <Trash2 className="h-4 w-4" />
                            </Button>
                          </div>
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            )}
          </motion.div>
        )}

        {/* Add/Edit Category Modal */}
        <Dialog open={showAddModal || showEditModal} onOpenChange={() => { setShowAddModal(false); setShowEditModal(false); setEditingItem(null); resetForm(); }}>
          <DialogContent className="bg-[#0a0a0a] border-white/10 text-white max-w-lg max-h-[90vh] overflow-y-auto">
            <DialogHeader>
              <DialogTitle>
                {showAddModal 
                  ? (language === 'ar' ? 'إضافة تصنيف جديد' : 'Add New Category')
                  : (language === 'ar' ? 'تعديل التصنيف' : 'Edit Category')
                }
              </DialogTitle>
              <DialogDescription className="text-[#71717A]">
                {language === 'ar' ? 'أدخل بيانات التصنيف' : 'Enter category details'}
              </DialogDescription>
            </DialogHeader>
            <div className="space-y-4 py-4">
              <div>
                <label className="block text-sm text-[#A1A1AA] mb-2">
                  {language === 'ar' ? 'الاسم بالعربي *' : 'Arabic Name *'}
                </label>
                <div className="flex gap-2">
                  <Input
                    value={formData.name_ar}
                    onChange={(e) => {
                      const value = e.target.value;
                      if (isArabicOnly(value)) {
                        setFormData(prev => ({ ...prev, name_ar: value }));
                      }
                    }}
                    onBlur={() => {
                      if (formData.name_ar.trim() && !formData.name_en.trim() && !translatingAr) {
                        translateArToEn();
                      }
                    }}
                    placeholder={language === 'ar' ? 'مثال: الملابس' : 'e.g., Clothing'}
                    className="bg-white/5 border-white/10 text-white flex-1"
                    dir="rtl"
                    data-testid="category-name-ar"
                  />
                  <TranslateButton
                    sourceText={formData.name_ar}
                    direction="ar_to_en"
                    onTranslated={(t) => setFormData(prev => ({ ...prev, name_en: t }))}
                    testId="cat-translate-ar-to-en"
                  />
                </div>
                <p className="text-xs text-[#606060] mt-1">
                  {language === 'ar' ? 'الحروف العربية فقط — تُترجم تلقائياً عند الخروج من الحقل أو اضغط على أيقونة الترجمة' : 'Arabic characters only — auto-translates on blur or click translate icon'}
                </p>
              </div>
              <div>
                <label className="block text-sm text-[#808080] mb-2">
                  {language === 'ar' ? 'الاسم بالإنجليزي *' : 'English Name *'}
                </label>
                <div className="flex gap-2">
                  <Input
                    value={formData.name_en}
                    onChange={(e) => {
                      const value = e.target.value;
                      if (isEnglishOnly(value)) {
                        setFormData(prev => ({ ...prev, name_en: value }));
                      }
                    }}
                    onBlur={() => {
                      if (formData.name_en.trim() && !formData.name_ar.trim() && !translatingEn) {
                        translateEnToAr();
                      }
                    }}
                    placeholder="e.g., Clothing"
                    className="bg-[#0a0a0a] border-[#2a2a4e] text-white flex-1"
                    dir="ltr"
                    data-testid="category-name-en"
                  />
                  <TranslateButton
                    sourceText={formData.name_en}
                    direction="en_to_ar"
                    onTranslated={(t) => setFormData(prev => ({ ...prev, name_ar: t }))}
                    testId="cat-translate-en-to-ar"
                  />
                </div>
                <p className="text-xs text-[#606060] mt-1">
                  {language === 'ar' ? 'الحروف الإنجليزية فقط — تُترجم تلقائياً عند الخروج من الحقل أو اضغط على أيقونة الترجمة' : 'English characters only — auto-translates on blur or click translate icon'}
                </p>
              </div>
              {activeView === 'categories' && (
                <div>
                  <label className="block text-sm text-[#808080] mb-2">
                    {language === 'ar' ? 'صورة التصنيف *' : 'Category Image *'}
                  </label>
                  <div 
                    onClick={() => fileInputRef.current?.click()}
                    className={`border-2 border-dashed rounded-xl p-6 text-center cursor-pointer transition-colors ${
                      formData.image ? 'border-emerald-500' : 'border-[#2a2a4e] hover:border-emerald-500/50'
                    }`}
                  >
                    {formData.image ? (
                      <div className="relative">
                        <img src={formData.image} alt="Preview" className="w-32 h-32 object-cover rounded-lg mx-auto" />
                        <button
                          onClick={(e) => {
                            e.stopPropagation();
                            setFormData(prev => ({ ...prev, image: '' }));
                          }}
                          className="absolute top-0 right-1/2 translate-x-16 -translate-y-2 w-6 h-6 bg-red-500 rounded-full flex items-center justify-center"
                        >
                          <X className="h-4 w-4 text-white" />
                        </button>
                      </div>
                    ) : (
                      <>
                        <ImageIcon className="h-10 w-10 text-[#808080] mx-auto mb-2" />
                        <p className="text-sm text-[#808080]">
                          {language === 'ar' ? 'اضغط لرفع صورة (مطلوب)' : 'Click to upload image (required)'}
                        </p>
                        <p className="text-xs text-[#606060] mt-1">
                          {language === 'ar' ? 'الحد الأقصى 2 ميجا' : 'Max 2MB'}
                        </p>
                      </>
                    )}
                  </div>
                  <input
                    ref={fileInputRef}
                    type="file"
                    accept="image/*"
                    onChange={handleImageUpload}
                    className="hidden"
                  />
                </div>
              )}

              {/* التصنيفات الفرعية - فقط عند إضافة تصنيف جديد */}
              {showAddModal && activeView === 'categories' && (
                <div className="border-t border-[#2a2a4e] pt-4">
                  <div className="flex items-center justify-between mb-3">
                    <label className="text-sm font-medium text-white">
                      {language === 'ar' ? 'التصنيفات الفرعية *' : 'Subcategories *'}
                    </label>
                    <Button
                      type="button"
                      onClick={addNewSubcategoryField}
                      variant="ghost"
                      size="sm"
                      className="text-emerald-400 hover:text-emerald-300 hover:bg-emerald-500/10"
                      data-testid="add-subcategory-btn"
                    >
                      <Plus className="h-4 w-4 me-1" />
                      {language === 'ar' ? 'إضافة' : 'Add'}
                    </Button>
                  </div>
                  <p className="text-xs text-[#606060] mb-3">
                    {language === 'ar' ? 'يجب إضافة تصنيف فرعي واحد على الأقل' : 'At least one subcategory is required'}
                  </p>
                  <div className="space-y-3 max-h-48 overflow-y-auto">
                    {newSubcategories.map((sub, index) => (
                      <div key={sub._uid || `sub-fallback-${index}`} className="bg-[#0a0a0a] rounded-lg p-3 border border-[#2a2a4e]">
                        <div className="flex items-center justify-between mb-2">
                          <span className="text-xs text-[#808080]">
                            {language === 'ar' ? `تصنيف فرعي ${index + 1}` : `Subcategory ${index + 1}`}
                          </span>
                          {newSubcategories.length > 1 && (
                            <button
                              type="button"
                              onClick={() => removeSubcategoryField(index)}
                              className="text-red-400 hover:text-red-300"
                              data-testid={`subcategory-${index}-remove-btn`}
                            >
                              <X className="h-4 w-4" />
                            </button>
                          )}
                        </div>
                        <div className="space-y-2">
                          {/* الاسم العربي مع زر الترجمة */}
                          <div className="flex gap-2">
                            <Input
                              value={sub.name_ar}
                              onChange={(e) => {
                                const value = e.target.value;
                                if (isArabicOnly(value)) {
                                  updateSubcategoryField(index, 'name_ar', value);
                                }
                              }}
                              onBlur={() => {
                                const s = newSubcategories[index];
                                if (s && s.name_ar.trim() && !s.name_en.trim() && !translatingSubAr[index]) {
                                  translateSubArToEn(index);
                                }
                              }}
                              placeholder={language === 'ar' ? 'الاسم بالعربي' : 'Arabic name'}
                              className="bg-[#1a1a2e] border-[#2a2a4e] text-white text-sm flex-1"
                              dir="rtl"
                              data-testid={`subcategory-${index}-ar`}
                            />
                            <TranslateButton
                              sourceText={sub.name_ar}
                              direction="ar_to_en"
                              onTranslated={(t) => updateSubcategoryField(index, 'name_en', t)}
                              testId={`cat-sub-translate-ar-to-en-${index}`}
                            />
                          </div>
                          {/* الاسم الإنجليزي مع زر الترجمة */}
                          <div className="flex gap-2">
                            <Input
                              value={sub.name_en}
                              onChange={(e) => {
                                const value = e.target.value;
                                if (isEnglishOnly(value)) {
                                  updateSubcategoryField(index, 'name_en', value);
                                }
                              }}
                              onBlur={() => {
                                const s = newSubcategories[index];
                                if (s && s.name_en.trim() && !s.name_ar.trim() && !translatingSubEn[index]) {
                                  translateSubEnToAr(index);
                                }
                              }}
                              placeholder={language === 'ar' ? 'الاسم بالإنجليزي' : 'English name'}
                              className="bg-[#1a1a2e] border-[#2a2a4e] text-white text-sm flex-1"
                              dir="ltr"
                              data-testid={`subcategory-${index}-en`}
                            />
                            <TranslateButton
                              sourceText={sub.name_en}
                              direction="en_to_ar"
                              onTranslated={(t) => updateSubcategoryField(index, 'name_ar', t)}
                              testId={`cat-sub-translate-en-to-ar-${index}`}
                            />
                          </div>
                        </div>
                      </div>
                    ))}
                  </div>
                </div>
              )}

              <div className="flex items-center justify-between">
                <label className="text-sm text-[#808080]">
                  {language === 'ar' ? 'نشط' : 'Active'}
                </label>
                <Switch
                  checked={formData.is_active}
                  onCheckedChange={(checked) => setFormData(prev => ({ ...prev, is_active: checked }))}
                  className="data-[state=checked]:bg-emerald-500"
                />
              </div>
            </div>
            <DialogFooter className="gap-2">
              <Button
                onClick={() => { setShowAddModal(false); setShowEditModal(false); setEditingItem(null); resetForm(); }}
                variant="outline"
                className="border-[#2a2a4e] text-white hover:bg-[#2a2a4e]"
              >
                {language === 'ar' ? 'إلغاء' : 'Cancel'}
              </Button>
              <Button
                onClick={activeView === 'categories' 
                  ? (showAddModal ? addCategory : updateCategory)
                  : (showEditModal ? updateSubcategory : addSubcategory)
                }
                disabled={saving}
                className="bg-emerald-500 hover:bg-emerald-600 text-white"
                data-testid="save-category-btn"
              >
                {saving ? (
                  <Loader2 className="h-4 w-4 animate-spin" />
                ) : (
                  language === 'ar' ? 'حفظ' : 'Save'
                )}
              </Button>
            </DialogFooter>
          </DialogContent>
        </Dialog>

        {/* Add Subcategory Modal */}
        <Dialog open={showSubcategoryModal} onOpenChange={() => { setShowSubcategoryModal(false); resetForm(); }}>
          <DialogContent className="bg-[#1a1a2e] border-[#2a2a4e] text-white max-w-md">
            <DialogHeader>
              <DialogTitle>
                {language === 'ar' ? 'إضافة تصنيف فرعي جديد' : 'Add New Subcategory'}
              </DialogTitle>
              <DialogDescription className="text-[#808080]">
                {language === 'ar' 
                  ? `إضافة تصنيف فرعي لـ "${selectedCategory?.name_ar}"`
                  : `Add subcategory to "${selectedCategory?.name_en}"`
                }
              </DialogDescription>
            </DialogHeader>
            <div className="space-y-4 py-4">
              <div>
                <label className="block text-sm text-[#808080] mb-2">
                  {language === 'ar' ? 'الاسم بالعربي *' : 'Arabic Name *'}
                </label>
                <div className="flex gap-2">
                  <Input
                    value={formData.name_ar}
                    onChange={(e) => {
                      const value = e.target.value;
                      if (isArabicOnly(value)) {
                        setFormData(prev => ({ ...prev, name_ar: value }));
                      }
                    }}
                    placeholder={language === 'ar' ? 'مثال: رجالي' : 'e.g., Men'}
                    className="bg-[#0a0a0a] border-[#2a2a4e] text-white flex-1"
                    dir="rtl"
                    data-testid="subcategory-name-ar"
                  />
                  <TranslateButton
                    sourceText={formData.name_ar}
                    direction="ar_to_en"
                    onTranslated={(t) => setFormData(prev => ({ ...prev, name_en: t }))}
                    testId="subcat-translate-ar-to-en"
                  />
                </div>
                <p className="text-xs text-[#606060] mt-1">
                  {language === 'ar' ? 'الحروف العربية فقط' : 'Arabic characters only'}
                </p>
              </div>
              <div>
                <label className="block text-sm text-[#808080] mb-2">
                  {language === 'ar' ? 'الاسم بالإنجليزي *' : 'English Name *'}
                </label>
                <div className="flex gap-2">
                  <Input
                    value={formData.name_en}
                    onChange={(e) => {
                      const value = e.target.value;
                      if (isEnglishOnly(value)) {
                        setFormData(prev => ({ ...prev, name_en: value }));
                      }
                    }}
                    placeholder="e.g., Men"
                    className="bg-[#0a0a0a] border-[#2a2a4e] text-white flex-1"
                    dir="ltr"
                    data-testid="subcategory-name-en"
                  />
                  <TranslateButton
                    sourceText={formData.name_en}
                    direction="en_to_ar"
                    onTranslated={(t) => setFormData(prev => ({ ...prev, name_ar: t }))}
                    testId="subcat-translate-en-to-ar"
                  />
                </div>
                <p className="text-xs text-[#606060] mt-1">
                  {language === 'ar' ? 'الحروف الإنجليزية فقط' : 'English characters only'}
                </p>
              </div>
              <div className="flex items-center justify-between">
                <label className="text-sm text-[#808080]">
                  {language === 'ar' ? 'نشط' : 'Active'}
                </label>
                <Switch
                  checked={formData.is_active}
                  onCheckedChange={(checked) => setFormData(prev => ({ ...prev, is_active: checked }))}
                  className="data-[state=checked]:bg-emerald-500"
                />
              </div>
            </div>
            <DialogFooter className="gap-2">
              <Button
                onClick={() => { setShowSubcategoryModal(false); resetForm(); }}
                variant="outline"
                className="border-[#2a2a4e] text-white hover:bg-[#2a2a4e]"
              >
                {language === 'ar' ? 'إلغاء' : 'Cancel'}
              </Button>
              <Button
                onClick={addSubcategory}
                disabled={saving}
                className="bg-emerald-500 hover:bg-emerald-600 text-white"
                data-testid="save-subcategory-btn"
              >
                {saving ? (
                  <Loader2 className="h-4 w-4 animate-spin" />
                ) : (
                  language === 'ar' ? 'حفظ' : 'Save'
                )}
              </Button>
            </DialogFooter>
          </DialogContent>
        </Dialog>

        {/* Delete Confirmation Modal */}
        <Dialog open={showDeleteModal} onOpenChange={() => { setShowDeleteModal(false); setDeletingItem(null); }}>
          <DialogContent className="bg-[#0a0a0a] border-white/10 text-white max-w-md">
            <DialogHeader>
              <DialogTitle className="text-red-400">
                {language === 'ar' ? 'تأكيد الحذف' : 'Confirm Delete'}
              </DialogTitle>
              <DialogDescription className="text-[#71717A]">
                {activeView === 'categories'
                  ? (language === 'ar' 
                      ? `هل أنت متأكد من حذف "${deletingItem?.name_ar}"؟ سيتم حذف جميع التصنيفات الفرعية أيضاً.`
                      : `Are you sure you want to delete "${deletingItem?.name_en}"? All subcategories will also be deleted.`
                    )
                  : (language === 'ar'
                      ? `هل أنت متأكد من حذف "${deletingItem?.name_ar}"؟`
                      : `Are you sure you want to delete "${deletingItem?.name_en}"?`
                    )
                }
              </DialogDescription>
            </DialogHeader>
            <DialogFooter className="gap-2 mt-4">
              <Button
                onClick={() => { setShowDeleteModal(false); setDeletingItem(null); }}
                variant="outline"
                className="border-white/10 text-white hover:bg-white/5"
              >
                {language === 'ar' ? 'إلغاء' : 'Cancel'}
              </Button>
              <Button
                onClick={activeView === 'categories' ? deleteCategory : deleteSubcategory}
                disabled={saving}
                className="bg-red-500 hover:bg-red-600 text-white"
                data-testid="confirm-delete-btn"
              >
                {saving ? (
                  <Loader2 className="h-4 w-4 animate-spin" />
                ) : (
                  language === 'ar' ? 'حذف' : 'Delete'
                )}
              </Button>
            </DialogFooter>
          </DialogContent>
        </Dialog>
        </div>
      </main>
    </DashboardLayout>
  );
}
